export interface MusicNote {
  freq: number;
  beat: number;
  dur: number;
  vel: number;
}

export interface TrackDef {
  bpm: number;
  beatsPerLoop: number;
  bass: MusicNote[];
  pad: MusicNote[];
  melody: MusicNote[];
}

// Wetland — Eb major, 76 BPM. Stately flowing melody inspired by Handel's Water Music.
// Dotted rhythms with strong intervals (4ths/5ths), continuous forward motion.
const WETLAND: TrackDef = {
  bpm: 76,
  beatsPerLoop: 32,
  bass: [
    // Flowing walking bass — steady forward motion like a current
    { freq: 155.6, beat: 0, dur: 1, vel: 0.28 },
    { freq: 174.6, beat: 1, dur: 1, vel: 0.24 },
    { freq: 155.6, beat: 2, dur: 1, vel: 0.26 },
    { freq: 130.8, beat: 3, dur: 1, vel: 0.24 },
    { freq: 116.5, beat: 4, dur: 1, vel: 0.28 },
    { freq: 130.8, beat: 5, dur: 1, vel: 0.24 },
    { freq: 155.6, beat: 6, dur: 1, vel: 0.26 },
    { freq: 174.6, beat: 7, dur: 1, vel: 0.24 },
    { freq: 155.6, beat: 8, dur: 1, vel: 0.28 },
    { freq: 130.8, beat: 9, dur: 1, vel: 0.24 },
    { freq: 116.5, beat: 10, dur: 1, vel: 0.26 },
    { freq: 130.8, beat: 11, dur: 1, vel: 0.24 },
    { freq: 155.6, beat: 12, dur: 1, vel: 0.28 },
    { freq: 174.6, beat: 13, dur: 1, vel: 0.24 },
    { freq: 196, beat: 14, dur: 1, vel: 0.26 },
    { freq: 174.6, beat: 15, dur: 1, vel: 0.24 },
    { freq: 155.6, beat: 16, dur: 1, vel: 0.28 },
    { freq: 174.6, beat: 17, dur: 1, vel: 0.24 },
    { freq: 155.6, beat: 18, dur: 1, vel: 0.26 },
    { freq: 130.8, beat: 19, dur: 1, vel: 0.24 },
    { freq: 116.5, beat: 20, dur: 1, vel: 0.28 },
    { freq: 130.8, beat: 21, dur: 1, vel: 0.24 },
    { freq: 155.6, beat: 22, dur: 1, vel: 0.26 },
    { freq: 174.6, beat: 23, dur: 1, vel: 0.24 },
    { freq: 196, beat: 24, dur: 1, vel: 0.28 },
    { freq: 174.6, beat: 25, dur: 1, vel: 0.24 },
    { freq: 155.6, beat: 26, dur: 1, vel: 0.26 },
    { freq: 130.8, beat: 27, dur: 1, vel: 0.24 },
    { freq: 116.5, beat: 28, dur: 1, vel: 0.28 },
    { freq: 130.8, beat: 29, dur: 1, vel: 0.24 },
    { freq: 146.8, beat: 30, dur: 1, vel: 0.26 },
    { freq: 155.6, beat: 31, dur: 1, vel: 0.24 },
  ],
  pad: [
    { freq: 311.1, beat: 0, dur: 8, vel: 0.08 },
    { freq: 392, beat: 0, dur: 8, vel: 0.06 },
    { freq: 349.2, beat: 8, dur: 8, vel: 0.08 },
    { freq: 466.2, beat: 8, dur: 8, vel: 0.06 },
    { freq: 311.1, beat: 16, dur: 8, vel: 0.08 },
    { freq: 392, beat: 16, dur: 8, vel: 0.06 },
    { freq: 349.2, beat: 24, dur: 8, vel: 0.08 },
    { freq: 466.2, beat: 24, dur: 8, vel: 0.06 },
  ],
  melody: [
    // Phrase 1: Stately dotted rhythm — Eb leaps up a 5th to Bb, then steps down
    { freq: 622.3, beat: 0, dur: 1.5, vel: 0.14 },
    { freq: 932.3, beat: 1.5, dur: 0.5, vel: 0.11 },
    { freq: 932.3, beat: 2, dur: 1.5, vel: 0.14 },
    { freq: 784, beat: 3.5, dur: 0.5, vel: 0.11 },
    { freq: 784, beat: 4, dur: 1.5, vel: 0.13 },
    { freq: 698.5, beat: 5.5, dur: 0.5, vel: 0.11 },
    { freq: 622.3, beat: 6, dur: 2, vel: 0.13 },
    // Phrase 2: Rising — leap up a 4th, step up, leap again
    { freq: 698.5, beat: 8, dur: 1.5, vel: 0.14 },
    { freq: 932.3, beat: 9.5, dur: 0.5, vel: 0.11 },
    { freq: 932.3, beat: 10, dur: 1.5, vel: 0.14 },
    { freq: 1046.5, beat: 11.5, dur: 0.5, vel: 0.12 },
    { freq: 1046.5, beat: 12, dur: 1.5, vel: 0.15 },
    { freq: 932.3, beat: 13.5, dur: 0.5, vel: 0.11 },
    { freq: 784, beat: 14, dur: 2, vel: 0.13 },
    // Phrase 3: repeat of phrase 1 (recognition)
    { freq: 622.3, beat: 16, dur: 1.5, vel: 0.14 },
    { freq: 932.3, beat: 17.5, dur: 0.5, vel: 0.11 },
    { freq: 932.3, beat: 18, dur: 1.5, vel: 0.14 },
    { freq: 784, beat: 19.5, dur: 0.5, vel: 0.11 },
    { freq: 784, beat: 20, dur: 1.5, vel: 0.13 },
    { freq: 698.5, beat: 21.5, dur: 0.5, vel: 0.11 },
    { freq: 622.3, beat: 22, dur: 2, vel: 0.13 },
    // Phrase 4: Majestic resolution — rising scale then settling
    { freq: 622.3, beat: 24, dur: 1, vel: 0.13 },
    { freq: 698.5, beat: 25, dur: 1, vel: 0.13 },
    { freq: 784, beat: 26, dur: 1, vel: 0.14 },
    { freq: 932.3, beat: 27, dur: 1.5, vel: 0.15 },
    { freq: 784, beat: 28.5, dur: 0.5, vel: 0.11 },
    { freq: 698.5, beat: 29, dur: 1, vel: 0.13 },
    { freq: 622.3, beat: 30, dur: 2, vel: 0.14 },
  ],
};

// Forest — C major, 108 BPM. Wood-elf whistle with skipping dotted rhythm (A-A-B-A).
// Influence: Kokiri Forest's casual whistling feel + Nutcracker Chinese Dance staccato runs.
const FOREST: TrackDef = {
  bpm: 108,
  beatsPerLoop: 32,
  bass: [
    // Sparse — just root on 1 and 3, stay out of the melody's way
    { freq: 130.8, beat: 0, dur: 1, vel: 0.25 },
    { freq: 130.8, beat: 2, dur: 1, vel: 0.2 },
    { freq: 130.8, beat: 4, dur: 1, vel: 0.25 },
    { freq: 130.8, beat: 6, dur: 1, vel: 0.2 },
    { freq: 146.8, beat: 8, dur: 1, vel: 0.25 },
    { freq: 146.8, beat: 10, dur: 1, vel: 0.2 },
    { freq: 130.8, beat: 12, dur: 1, vel: 0.25 },
    { freq: 130.8, beat: 14, dur: 1, vel: 0.2 },
    { freq: 130.8, beat: 16, dur: 1, vel: 0.25 },
    { freq: 130.8, beat: 18, dur: 1, vel: 0.2 },
    { freq: 130.8, beat: 20, dur: 1, vel: 0.25 },
    { freq: 130.8, beat: 22, dur: 1, vel: 0.2 },
    { freq: 110, beat: 24, dur: 1, vel: 0.25 },
    { freq: 130.8, beat: 26, dur: 1, vel: 0.2 },
    { freq: 130.8, beat: 28, dur: 1, vel: 0.25 },
    { freq: 146.8, beat: 30, dur: 1, vel: 0.2 },
  ],
  pad: [
    // Minimal — just a whisper on chord changes to keep it airy
    { freq: 261.6, beat: 0, dur: 4, vel: 0.06 },
    { freq: 329.6, beat: 0, dur: 4, vel: 0.04 },
    { freq: 293.7, beat: 8, dur: 4, vel: 0.06 },
    { freq: 349.2, beat: 8, dur: 4, vel: 0.04 },
    { freq: 261.6, beat: 16, dur: 4, vel: 0.06 },
    { freq: 329.6, beat: 16, dur: 4, vel: 0.04 },
    { freq: 220, beat: 24, dur: 4, vel: 0.06 },
    { freq: 261.6, beat: 24, dur: 4, vel: 0.04 },
  ],
  melody: [
    // A phrase: skipping dotted rhythm whistle (long-short, long-short)
    // E5 - G5 - A5 - G5 pattern with dotted-eighth + sixteenth feel
    { freq: 659.3, beat: 0, dur: 1, vel: 0.14 },
    { freq: 784, beat: 1.25, dur: 0.4, vel: 0.11 },
    { freq: 880, beat: 2, dur: 1, vel: 0.14 },
    { freq: 784, beat: 3.25, dur: 0.4, vel: 0.11 },
    { freq: 659.3, beat: 4, dur: 1, vel: 0.13 },
    { freq: 523.3, beat: 5.25, dur: 0.4, vel: 0.1 },
    { freq: 659.3, beat: 6, dur: 1.5, vel: 0.13 },
    // A repeat (same whistle, slightly varied ending)
    { freq: 659.3, beat: 8, dur: 1, vel: 0.14 },
    { freq: 784, beat: 9.25, dur: 0.4, vel: 0.11 },
    { freq: 880, beat: 10, dur: 1, vel: 0.14 },
    { freq: 784, beat: 11.25, dur: 0.4, vel: 0.11 },
    { freq: 880, beat: 12, dur: 1, vel: 0.13 },
    { freq: 1046.5, beat: 13.25, dur: 0.4, vel: 0.12 },
    { freq: 880, beat: 14, dur: 1.5, vel: 0.13 },
    // B section: staccato repeated-note run (Nutcracker influence)
    // Rapid plink-plink-plink then scalar dash
    { freq: 784, beat: 16, dur: 0.3, vel: 0.13 },
    { freq: 784, beat: 16.5, dur: 0.3, vel: 0.12 },
    { freq: 784, beat: 17, dur: 0.3, vel: 0.11 },
    { freq: 784, beat: 17.5, dur: 0.3, vel: 0.12 },
    // Quick scalar run up: A-B-C-D-E
    { freq: 880, beat: 18, dur: 0.25, vel: 0.12 },
    { freq: 988, beat: 18.3, dur: 0.25, vel: 0.12 },
    { freq: 1046.5, beat: 18.6, dur: 0.25, vel: 0.13 },
    { freq: 1175, beat: 18.9, dur: 0.25, vel: 0.13 },
    { freq: 1319, beat: 19.2, dur: 0.6, vel: 0.14 },
    // Plink-plink again (lower)
    { freq: 659.3, beat: 20, dur: 0.3, vel: 0.13 },
    { freq: 659.3, beat: 20.5, dur: 0.3, vel: 0.12 },
    { freq: 659.3, beat: 21, dur: 0.3, vel: 0.11 },
    { freq: 659.3, beat: 21.5, dur: 0.3, vel: 0.12 },
    // Quick scalar run down: E-D-C-B-A
    { freq: 1319, beat: 22, dur: 0.25, vel: 0.13 },
    { freq: 1175, beat: 22.3, dur: 0.25, vel: 0.12 },
    { freq: 1046.5, beat: 22.6, dur: 0.25, vel: 0.12 },
    { freq: 988, beat: 22.9, dur: 0.25, vel: 0.12 },
    { freq: 880, beat: 23.2, dur: 0.6, vel: 0.13 },
    // A return: back to the whistle (recognition/resolution)
    { freq: 659.3, beat: 24, dur: 1, vel: 0.14 },
    { freq: 784, beat: 25.25, dur: 0.4, vel: 0.11 },
    { freq: 880, beat: 26, dur: 1, vel: 0.14 },
    { freq: 784, beat: 27.25, dur: 0.4, vel: 0.11 },
    { freq: 659.3, beat: 28, dur: 1, vel: 0.13 },
    { freq: 587.3, beat: 29.25, dur: 0.4, vel: 0.1 },
    { freq: 523.3, beat: 30, dur: 2, vel: 0.13 },
  ],
};

// Alpine — C major, 72 BPM. Grand fanfare inspired by Copland's Fanfare for the Common Man.
// Repeated-note declarations, dotted figures, ascending runs — brass-section energy.
const ALPINE: TrackDef = {
  bpm: 72,
  beatsPerLoop: 32,
  bass: [
    // Timpani rolls and hits — driving the fanfare forward
    { freq: 98, beat: 0, dur: 0.8, vel: 0.3 },
    { freq: 98, beat: 1, dur: 0.8, vel: 0.25 },
    { freq: 98, beat: 2, dur: 0.8, vel: 0.28 },
    { freq: 98, beat: 4, dur: 1.5, vel: 0.32 },
    { freq: 98, beat: 6, dur: 0.8, vel: 0.22 },
    { freq: 110, beat: 8, dur: 0.8, vel: 0.3 },
    { freq: 110, beat: 9, dur: 0.8, vel: 0.25 },
    { freq: 110, beat: 10, dur: 0.8, vel: 0.28 },
    { freq: 98, beat: 12, dur: 1.5, vel: 0.32 },
    { freq: 98, beat: 14, dur: 0.8, vel: 0.22 },
    { freq: 98, beat: 16, dur: 0.8, vel: 0.3 },
    { freq: 98, beat: 17, dur: 0.8, vel: 0.28 },
    { freq: 110, beat: 18, dur: 0.8, vel: 0.28 },
    { freq: 130.8, beat: 19, dur: 0.8, vel: 0.3 },
    { freq: 98, beat: 20, dur: 1.5, vel: 0.32 },
    { freq: 98, beat: 22, dur: 0.8, vel: 0.25 },
    { freq: 130.8, beat: 24, dur: 0.8, vel: 0.32 },
    { freq: 130.8, beat: 25, dur: 0.8, vel: 0.28 },
    { freq: 110, beat: 26, dur: 0.8, vel: 0.28 },
    { freq: 98, beat: 27, dur: 0.8, vel: 0.25 },
    { freq: 98, beat: 28, dur: 1.5, vel: 0.32 },
    { freq: 110, beat: 30, dur: 0.8, vel: 0.25 },
    { freq: 98, beat: 31, dur: 0.8, vel: 0.22 },
  ],
  pad: [
    // Open 5ths — vast harmonic bed
    { freq: 261.6, beat: 0, dur: 8, vel: 0.09 },
    { freq: 392, beat: 0, dur: 8, vel: 0.07 },
    { freq: 293.7, beat: 8, dur: 8, vel: 0.09 },
    { freq: 440, beat: 8, dur: 8, vel: 0.07 },
    { freq: 261.6, beat: 16, dur: 8, vel: 0.09 },
    { freq: 392, beat: 16, dur: 8, vel: 0.07 },
    { freq: 349.2, beat: 24, dur: 8, vel: 0.09 },
    { freq: 523.3, beat: 24, dur: 8, vel: 0.07 },
  ],
  melody: [
    // Phrase 1: Repeated G declarations, then leap up — "hear ye!"
    { freq: 392, beat: 0, dur: 0.8, vel: 0.14 },
    { freq: 392, beat: 1, dur: 0.8, vel: 0.14 },
    { freq: 392, beat: 2, dur: 1.5, vel: 0.15 },
    // Dotted leap up to D
    { freq: 587.3, beat: 4, dur: 1.5, vel: 0.16 },
    { freq: 523.3, beat: 5.5, dur: 0.5, vel: 0.12 },
    { freq: 587.3, beat: 6, dur: 2, vel: 0.15 },
    // Phrase 2: Ascending run — C-D-E-G (fanfare call)
    { freq: 523.3, beat: 8, dur: 0.7, vel: 0.14 },
    { freq: 587.3, beat: 8.75, dur: 0.7, vel: 0.14 },
    { freq: 659.3, beat: 9.5, dur: 0.7, vel: 0.15 },
    { freq: 784, beat: 10.25, dur: 1.5, vel: 0.16 },
    // Dotted answer: high G down to E
    { freq: 784, beat: 12, dur: 1.5, vel: 0.15 },
    { freq: 659.3, beat: 13.5, dur: 0.5, vel: 0.12 },
    { freq: 659.3, beat: 14, dur: 2, vel: 0.14 },
    // Phrase 3: Building — repeated E's, then triumphant ascent
    { freq: 659.3, beat: 16, dur: 0.7, vel: 0.14 },
    { freq: 659.3, beat: 16.75, dur: 0.7, vel: 0.14 },
    { freq: 659.3, beat: 17.5, dur: 0.7, vel: 0.15 },
    // Run up: E-G-A-B-C (to the peak!)
    { freq: 659.3, beat: 18.5, dur: 0.5, vel: 0.14 },
    { freq: 784, beat: 19, dur: 0.5, vel: 0.15 },
    { freq: 880, beat: 19.5, dur: 0.5, vel: 0.15 },
    { freq: 988, beat: 20, dur: 0.5, vel: 0.15 },
    { freq: 1046.5, beat: 20.5, dur: 2, vel: 0.16 },
    // Dotted descent from peak
    { freq: 1046.5, beat: 22.5, dur: 0.5, vel: 0.12 },
    { freq: 784, beat: 23, dur: 1, vel: 0.14 },
    // Phrase 4: Majestic resolution — dotted declarations descending home
    { freq: 784, beat: 24, dur: 1.5, vel: 0.15 },
    { freq: 659.3, beat: 25.5, dur: 0.5, vel: 0.12 },
    { freq: 659.3, beat: 26, dur: 1.5, vel: 0.15 },
    { freq: 587.3, beat: 27.5, dur: 0.5, vel: 0.12 },
    { freq: 587.3, beat: 28, dur: 1.5, vel: 0.14 },
    { freq: 523.3, beat: 29.5, dur: 0.5, vel: 0.12 },
    { freq: 523.3, beat: 30, dur: 2, vel: 0.15 },
  ],
};

// Coral — B minor, 80 BPM. Inspired by Mendelssohn's Hebrides Overture (Fingal's Cave).
// Rolling eighth-note wave in bass, lyrical melody rides on top in the same pulse.
const CORAL: TrackDef = {
  bpm: 80,
  beatsPerLoop: 32,
  bass: [
    // Rolling wave: alternating root-5th on every half beat (eighth notes)
    // Bm wave (beats 0-7)
    { freq: 123.5, beat: 0, dur: 0.4, vel: 0.26 },
    { freq: 185, beat: 0.5, dur: 0.4, vel: 0.2 },
    { freq: 123.5, beat: 1, dur: 0.4, vel: 0.24 },
    { freq: 185, beat: 1.5, dur: 0.4, vel: 0.2 },
    { freq: 123.5, beat: 2, dur: 0.4, vel: 0.26 },
    { freq: 185, beat: 2.5, dur: 0.4, vel: 0.2 },
    { freq: 123.5, beat: 3, dur: 0.4, vel: 0.24 },
    { freq: 185, beat: 3.5, dur: 0.4, vel: 0.2 },
    { freq: 123.5, beat: 4, dur: 0.4, vel: 0.26 },
    { freq: 185, beat: 4.5, dur: 0.4, vel: 0.2 },
    { freq: 123.5, beat: 5, dur: 0.4, vel: 0.24 },
    { freq: 185, beat: 5.5, dur: 0.4, vel: 0.2 },
    { freq: 123.5, beat: 6, dur: 0.4, vel: 0.26 },
    { freq: 185, beat: 6.5, dur: 0.4, vel: 0.2 },
    { freq: 123.5, beat: 7, dur: 0.4, vel: 0.24 },
    { freq: 185, beat: 7.5, dur: 0.4, vel: 0.2 },
    // G wave (beats 8-15)
    { freq: 98, beat: 8, dur: 0.4, vel: 0.26 },
    { freq: 146.8, beat: 8.5, dur: 0.4, vel: 0.2 },
    { freq: 98, beat: 9, dur: 0.4, vel: 0.24 },
    { freq: 146.8, beat: 9.5, dur: 0.4, vel: 0.2 },
    { freq: 98, beat: 10, dur: 0.4, vel: 0.26 },
    { freq: 146.8, beat: 10.5, dur: 0.4, vel: 0.2 },
    { freq: 98, beat: 11, dur: 0.4, vel: 0.24 },
    { freq: 146.8, beat: 11.5, dur: 0.4, vel: 0.2 },
    { freq: 98, beat: 12, dur: 0.4, vel: 0.26 },
    { freq: 146.8, beat: 12.5, dur: 0.4, vel: 0.2 },
    { freq: 98, beat: 13, dur: 0.4, vel: 0.24 },
    { freq: 146.8, beat: 13.5, dur: 0.4, vel: 0.2 },
    { freq: 98, beat: 14, dur: 0.4, vel: 0.26 },
    { freq: 146.8, beat: 14.5, dur: 0.4, vel: 0.2 },
    { freq: 98, beat: 15, dur: 0.4, vel: 0.24 },
    { freq: 146.8, beat: 15.5, dur: 0.4, vel: 0.2 },
    // Bm wave building (beats 16-21)
    { freq: 123.5, beat: 16, dur: 0.4, vel: 0.28 },
    { freq: 185, beat: 16.5, dur: 0.4, vel: 0.22 },
    { freq: 123.5, beat: 17, dur: 0.4, vel: 0.26 },
    { freq: 185, beat: 17.5, dur: 0.4, vel: 0.22 },
    { freq: 123.5, beat: 18, dur: 0.4, vel: 0.28 },
    { freq: 185, beat: 18.5, dur: 0.4, vel: 0.22 },
    { freq: 123.5, beat: 19, dur: 0.4, vel: 0.26 },
    { freq: 185, beat: 19.5, dur: 0.4, vel: 0.22 },
    // A wave — swell peak (beats 20-23)
    { freq: 110, beat: 20, dur: 0.4, vel: 0.3 },
    { freq: 164.8, beat: 20.5, dur: 0.4, vel: 0.24 },
    { freq: 110, beat: 21, dur: 0.4, vel: 0.28 },
    { freq: 164.8, beat: 21.5, dur: 0.4, vel: 0.24 },
    { freq: 110, beat: 22, dur: 0.4, vel: 0.3 },
    { freq: 164.8, beat: 22.5, dur: 0.4, vel: 0.24 },
    { freq: 110, beat: 23, dur: 0.4, vel: 0.28 },
    { freq: 164.8, beat: 23.5, dur: 0.4, vel: 0.24 },
    // Receding Bm wave (beats 24-31) — fading
    { freq: 123.5, beat: 24, dur: 0.4, vel: 0.24 },
    { freq: 185, beat: 24.5, dur: 0.4, vel: 0.18 },
    { freq: 123.5, beat: 25, dur: 0.4, vel: 0.22 },
    { freq: 185, beat: 25.5, dur: 0.4, vel: 0.17 },
    { freq: 123.5, beat: 26, dur: 0.4, vel: 0.2 },
    { freq: 185, beat: 26.5, dur: 0.4, vel: 0.16 },
    { freq: 123.5, beat: 27, dur: 0.4, vel: 0.19 },
    { freq: 185, beat: 27.5, dur: 0.4, vel: 0.15 },
    { freq: 123.5, beat: 28, dur: 0.4, vel: 0.18 },
    { freq: 185, beat: 28.5, dur: 0.4, vel: 0.14 },
    { freq: 123.5, beat: 29, dur: 0.4, vel: 0.17 },
    { freq: 185, beat: 29.5, dur: 0.4, vel: 0.13 },
    { freq: 123.5, beat: 30, dur: 0.4, vel: 0.16 },
    { freq: 185, beat: 30.5, dur: 0.4, vel: 0.13 },
    { freq: 123.5, beat: 31, dur: 0.4, vel: 0.15 },
    { freq: 185, beat: 31.5, dur: 0.4, vel: 0.12 },
  ],
  pad: [
    // Sustained minor chords — mysterious depth
    { freq: 246.9, beat: 0, dur: 8, vel: 0.08 },
    { freq: 293.7, beat: 0, dur: 8, vel: 0.06 },
    { freq: 370, beat: 0, dur: 8, vel: 0.05 },
    { freq: 196, beat: 8, dur: 8, vel: 0.08 },
    { freq: 246.9, beat: 8, dur: 8, vel: 0.06 },
    { freq: 293.7, beat: 8, dur: 8, vel: 0.05 },
    { freq: 246.9, beat: 16, dur: 8, vel: 0.09 },
    { freq: 293.7, beat: 16, dur: 8, vel: 0.07 },
    { freq: 370, beat: 16, dur: 8, vel: 0.05 },
    { freq: 220, beat: 24, dur: 8, vel: 0.08 },
    { freq: 261.6, beat: 24, dur: 8, vel: 0.06 },
    { freq: 329.6, beat: 24, dur: 8, vel: 0.05 },
  ],
  melody: [
    // Lyrical theme on the same half-beat grid as the bass
    // Phrase 1 (hook): B4-D5 pickup, lands on F#5, steps to A, falls back
    { freq: 494, beat: 0, dur: 0.5, vel: 0.12 },
    { freq: 587.3, beat: 0.5, dur: 0.5, vel: 0.12 },
    { freq: 740, beat: 1, dur: 2.5, vel: 0.15 },
    { freq: 880, beat: 4, dur: 1.5, vel: 0.14 },
    { freq: 740, beat: 6, dur: 1.5, vel: 0.13 },
    // Phrase 2 (hook again, pushes higher)
    { freq: 494, beat: 8, dur: 0.5, vel: 0.12 },
    { freq: 587.3, beat: 8.5, dur: 0.5, vel: 0.12 },
    { freq: 740, beat: 9, dur: 2.5, vel: 0.15 },
    { freq: 880, beat: 12, dur: 1.5, vel: 0.14 },
    { freq: 988, beat: 14, dur: 1.5, vel: 0.15 },
    // Phrase 3 (swell): climbs urgently to D6
    { freq: 740, beat: 16, dur: 1, vel: 0.14 },
    { freq: 880, beat: 17, dur: 1, vel: 0.14 },
    { freq: 988, beat: 18, dur: 2, vel: 0.16 },
    { freq: 880, beat: 20, dur: 1, vel: 0.14 },
    { freq: 988, beat: 21, dur: 1, vel: 0.15 },
    { freq: 1175, beat: 22, dur: 2, vel: 0.16 },
    // Phrase 4 (recedes): hook descends, fading
    { freq: 988, beat: 24, dur: 1, vel: 0.13 },
    { freq: 880, beat: 25, dur: 1, vel: 0.12 },
    { freq: 740, beat: 26, dur: 2, vel: 0.12 },
    { freq: 587.3, beat: 28, dur: 2, vel: 0.11 },
    { freq: 494, beat: 30, dur: 2, vel: 0.1 },
  ],
};

// Savanna — D major, 100 BPM. Call-and-response: solo melody phrase, then full ensemble answers.
const SAVANNA: TrackDef = {
  bpm: 100,
  beatsPerLoop: 32,
  bass: [
    // SILENT during call (beats 0-3), then answers
    { freq: 146.8, beat: 4, dur: 0.8, vel: 0.3 },
    { freq: 146.8, beat: 5, dur: 0.8, vel: 0.25 },
    { freq: 185, beat: 6, dur: 0.8, vel: 0.28 },
    { freq: 146.8, beat: 7, dur: 0.8, vel: 0.25 },
    // SILENT during call (beats 8-11)
    { freq: 164.8, beat: 12, dur: 0.8, vel: 0.3 },
    { freq: 164.8, beat: 13, dur: 0.8, vel: 0.25 },
    { freq: 146.8, beat: 14, dur: 0.8, vel: 0.28 },
    { freq: 123.5, beat: 15, dur: 0.8, vel: 0.25 },
    // SILENT during call (beats 16-19)
    { freq: 146.8, beat: 20, dur: 0.8, vel: 0.3 },
    { freq: 185, beat: 21, dur: 0.8, vel: 0.28 },
    { freq: 146.8, beat: 22, dur: 0.8, vel: 0.25 },
    { freq: 146.8, beat: 23, dur: 0.8, vel: 0.28 },
    // SILENT during call (beats 24-27)
    { freq: 123.5, beat: 28, dur: 0.8, vel: 0.3 },
    { freq: 146.8, beat: 29, dur: 0.8, vel: 0.25 },
    { freq: 146.8, beat: 30, dur: 0.8, vel: 0.28 },
    { freq: 185, beat: 31, dur: 0.8, vel: 0.25 },
  ],
  pad: [
    // Only enters on response sections
    { freq: 293.7, beat: 4, dur: 4, vel: 0.1 },
    { freq: 370, beat: 4, dur: 4, vel: 0.07 },
    { freq: 329.6, beat: 12, dur: 4, vel: 0.1 },
    { freq: 440, beat: 12, dur: 4, vel: 0.07 },
    { freq: 293.7, beat: 20, dur: 4, vel: 0.1 },
    { freq: 370, beat: 20, dur: 4, vel: 0.07 },
    { freq: 247, beat: 28, dur: 4, vel: 0.1 },
    { freq: 370, beat: 28, dur: 4, vel: 0.07 },
  ],
  melody: [
    // CALL 1: Solo D-F#-A rising (unaccompanied)
    { freq: 587.3, beat: 0, dur: 0.8, vel: 0.14 },
    { freq: 740, beat: 1, dur: 0.8, vel: 0.13 },
    { freq: 880, beat: 2, dur: 1.5, vel: 0.15 },
    // RESPONSE 1: Echo of call, harmonized lower
    { freq: 880, beat: 4, dur: 0.6, vel: 0.12 },
    { freq: 740, beat: 5, dur: 0.6, vel: 0.11 },
    { freq: 587.3, beat: 6, dur: 1.5, vel: 0.12 },
    // CALL 2: Higher, more insistent — A-B-D
    { freq: 880, beat: 8, dur: 0.8, vel: 0.14 },
    { freq: 988, beat: 9, dur: 0.8, vel: 0.13 },
    { freq: 1175, beat: 10, dur: 1.5, vel: 0.15 },
    // RESPONSE 2: Descending echo with embellishment
    { freq: 1175, beat: 12, dur: 0.5, vel: 0.12 },
    { freq: 988, beat: 12.5, dur: 0.5, vel: 0.11 },
    { freq: 880, beat: 13, dur: 0.5, vel: 0.11 },
    { freq: 740, beat: 14, dur: 1.5, vel: 0.12 },
    // CALL 3: Peak — solo leaps to high D
    { freq: 1175, beat: 16, dur: 0.8, vel: 0.14 },
    { freq: 988, beat: 17, dur: 0.6, vel: 0.13 },
    { freq: 1175, beat: 18, dur: 0.8, vel: 0.15 },
    { freq: 1480, beat: 19, dur: 0.8, vel: 0.14 },
    // RESPONSE 3: Full ensemble cascades down
    { freq: 1480, beat: 20, dur: 0.4, vel: 0.13 },
    { freq: 1175, beat: 20.5, dur: 0.4, vel: 0.12 },
    { freq: 988, beat: 21, dur: 0.4, vel: 0.12 },
    { freq: 880, beat: 21.5, dur: 0.4, vel: 0.11 },
    { freq: 740, beat: 22, dur: 1.5, vel: 0.12 },
    // CALL 4: Resolving — gentle solo
    { freq: 587.3, beat: 24, dur: 1, vel: 0.13 },
    { freq: 740, beat: 25.5, dur: 1, vel: 0.12 },
    { freq: 587.3, beat: 27, dur: 0.8, vel: 0.13 },
    // RESPONSE 4: Warm resolution
    { freq: 587.3, beat: 28, dur: 0.5, vel: 0.11 },
    { freq: 494, beat: 29, dur: 0.5, vel: 0.1 },
    { freq: 587.3, beat: 30, dur: 2, vel: 0.12 },
  ],
};

// Menu — D major, 95 BPM. Light and inviting, airy pentatonic melody. No bass — just pad + melody.
const MENU: TrackDef = {
  bpm: 95,
  beatsPerLoop: 16,
  bass: [],
  pad: [
    { freq: 293.7, beat: 0, dur: 4, vel: 0.06 },
    { freq: 370, beat: 0, dur: 4, vel: 0.04 },
    { freq: 392, beat: 4, dur: 4, vel: 0.06 },
    { freq: 494, beat: 4, dur: 4, vel: 0.04 },
    { freq: 440, beat: 8, dur: 4, vel: 0.06 },
    { freq: 523.3, beat: 8, dur: 4, vel: 0.04 },
    { freq: 293.7, beat: 12, dur: 4, vel: 0.06 },
    { freq: 370, beat: 12, dur: 4, vel: 0.04 },
  ],
  melody: [
    // Bright, simple D major pentatonic — welcoming and easy
    { freq: 587.3, beat: 0, dur: 0.5, vel: 0.13 },
    { freq: 740, beat: 0.5, dur: 0.5, vel: 0.12 },
    { freq: 880, beat: 1, dur: 1.5, vel: 0.14 },
    { freq: 740, beat: 3, dur: 0.5, vel: 0.11 },
    { freq: 587.3, beat: 4, dur: 1.5, vel: 0.13 },
    // Step up and float
    { freq: 659.3, beat: 6, dur: 0.5, vel: 0.12 },
    { freq: 740, beat: 6.5, dur: 0.5, vel: 0.12 },
    { freq: 880, beat: 7, dur: 1, vel: 0.13 },
    // Gentle descent
    { freq: 740, beat: 8, dur: 1, vel: 0.13 },
    { freq: 659.3, beat: 9, dur: 1, vel: 0.12 },
    { freq: 587.3, beat: 10, dur: 1.5, vel: 0.13 },
    // Resolve home
    { freq: 494, beat: 12, dur: 0.5, vel: 0.11 },
    { freq: 587.3, beat: 13, dur: 2.5, vel: 0.13 },
  ],
};

export const TRACKS: TrackDef[] = [WETLAND, FOREST, ALPINE, CORAL, SAVANNA];
export const MENU_TRACK: TrackDef = MENU;

export function getTrackForLevel(level: number): TrackDef {
  return TRACKS[(level - 1) % TRACKS.length];
}
